import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { FIREBASE_AUTH, FIREBASE_DB } from "./firebase.config.js";
import { doc, getDoc } from "firebase/firestore";
import { useQuery } from "react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Briefcase,
  FileText,
  Users,
  Bell,
  PlusCircle,
  BarChart2,
  Building,
  Eye,
  XCircle,
  Clock,
  MessageSquare,
  Edit,
  Book,
} from "lucide-react";
import AdminLayout from "./layouts/AdminLayout";
import WebsiteFunctionalityPresentation from "./components/WebsiteFunctionalityPresentation";
import BannerManagement from "./components/BannerManagement";
import BulletinManagement from "./components/BulletinManagement";

const API_URL = process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const fetchDashboardData = async (dateRange) => {
  const response = await fetch(
    `${API_URL}/admin/dashboard-stats?range=${dateRange}`
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

const AdminDashboard = () => {
  const [dateRange] = useState("week");
  const [userName, setUserName] = useState("Admin");
  const [showFunctionality, setShowFunctionality] = useState(false);
  const [activeSection, setActiveSection] = useState("dashboard");

  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
  } = useQuery(
    ["dashboardStats", dateRange],
    () => fetchDashboardData(dateRange),
    { staleTime: 5 * 60 * 1000 }
  );

  const stats = useMemo(
    () => ({
      totalJobs: dashboardData?.totalJobs || 0,
      activeJobs: dashboardData?.activeJobs || 0,
      totalApplications: dashboardData?.totalApplications || 0,
      recentApplications: dashboardData?.recentApplications || 0,
    }),
    [dashboardData]
  );

  const fetchUserName = useCallback(async () => {
    const user = FIREBASE_AUTH.currentUser;
    if (user) {
      const userDoc = await getDoc(doc(FIREBASE_DB, "users", user.uid));
      if (userDoc.exists()) {
        setUserName(userDoc.data().name || "Admin");
      }
    }
  }, []);

  useEffect(() => {
    fetchUserName();
  }, [fetchUserName]);

  if (isDashboardLoading)
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );

  if (dashboardError)
    return (
      <div className="text-red-500 text-center p-4">
        {dashboardError.message}
      </div>
    );

  const chartData = [
    { name: "Total Jobs", value: stats.totalJobs },
    { name: "Active Jobs", value: stats.activeJobs },
    { name: "Total Applications", value: stats.totalApplications },
    { name: "Recent Applications", value: stats.recentApplications },
  ];

  const renderDashboardContent = () => (
    <>
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-sm sm:text-base text-gray-600">
          Monitor and manage all platform activities
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <StatCard 
          title="Total Jobs" 
          value={stats.totalJobs} 
          icon={<Briefcase className="w-8 h-8 text-orange-500" />}
          bgColor="bg-green-100"
        />
        <StatCard 
          title="Active Jobs" 
          value={stats.activeJobs} 
          icon={<Briefcase className="w-8 h-8 text-green-600" />}
          bgColor="bg-orange-100"
        />
        <StatCard 
          title="Total Applications" 
          value={stats.totalApplications} 
          icon={<FileText className="w-8 h-8 text-orange-500" />}
          bgColor="bg-green-100"
        />
        <StatCard 
          title="Recent Applications" 
          value={stats.recentApplications} 
          icon={<Clock className="w-8 h-8 text-green-600" />}
          bgColor="bg-orange-100"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6 border-b-4 border-orange-500">
            <h2 className="text-xl font-semibold text-green-800 mb-4 flex items-center">
              <Briefcase className="w-6 h-6 mr-2 text-orange-500" />
              Job Management
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ActionButton
                title="Manage Jobs"
                icon={<Briefcase className="h-5 w-5" />}
                link="/admin/job-listings"
                color="bg-green-600"
              />
              <ActionButton
                title="Post New Job"
                icon={<PlusCircle className="h-5 w-5" />}
                link="/post-job"
                color="bg-orange-500"
              />
              <ActionButton
                title="Analytics"
                icon={<BarChart2 className="h-5 w-5" />}
                link="/admin/analytics"
                color="bg-green-600"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-b-4 border-orange-500">
            <h2 className="text-xl font-semibold text-green-800 mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-orange-500" />
              Applications & Inquiries
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ActionButton
                title="Applications"
                icon={<FileText className="h-5 w-5" />}
                link="/admin/applications"
                color="bg-orange-500"
              />
              <ActionButton
                title="Employer Contacts"
                icon={<Building className="h-5 w-5" />}
                link="/admin/employer-contacts"
                color="bg-green-600"
              />
              <ActionButton
                title="General Inquiries"
                icon={<MessageSquare className="h-5 w-5" />}
                link="/admin/general-inquiries"
                color="bg-orange-500"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden border-b-4 border-orange-500">
            <div className="bg-green-700 p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white flex items-center">
                <Eye className="w-6 h-6 mr-2" />
                Website Functionality Overview
              </h2>
              <button
                onClick={() => setShowFunctionality(!showFunctionality)}
                className="text-white hover:text-accent transition-colors"
              >
                {showFunctionality ? (
                  <XCircle className="w-6 h-6" />
                ) : (
                  <Eye className="w-6 h-6" />
                )}
              </button>
            </div>
            {showFunctionality && <WebsiteFunctionalityPresentation />}
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6 border-b-4 border-orange-500">
            <h2 className="text-xl font-semibold text-green-800 mb-4">Quick Stats</h2>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} width={30} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-b-4 border-orange-500">
            <h2 className="text-xl font-semibold text-green-800 mb-4 flex items-center">
              <Edit className="w-6 h-6 mr-2 text-orange-500" />
              Content Management
            </h2>
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => setActiveSection("bulletins")}
                className="bg-green-600 text-white py-2 px-3 rounded-md hover:opacity-90 transition-opacity flex items-center justify-center text-sm whitespace-nowrap"
              >
                <FileText className="h-5 w-5 mr-2" />
                Manage Bulletins
              </button>
              <button
                onClick={() => setActiveSection("banners")}
                className="bg-orange-500 text-white py-2 px-3 rounded-md hover:opacity-90 transition-opacity flex items-center justify-center text-sm whitespace-nowrap"
              >
                <Edit className="h-5 w-5 mr-2" />
                Manage Banners
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeSection === "dashboard" && renderDashboardContent()}
        {activeSection === "banners" && (
          <>
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Banner Management</h1>
                  <p className="text-sm text-gray-600">Create and manage banners for the home page</p>
                </div>
                <button
                  onClick={() => setActiveSection("dashboard")}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Return to Dashboard
                </button>
              </div>
            </div>
            <BannerManagement />
          </>
        )}
        {activeSection === "bulletins" && (
          <>
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Bulletin Management</h1>
                  <p className="text-sm text-gray-600">Create and manage bulletin posts</p>
                </div>
                <button
                  onClick={() => setActiveSection("dashboard")}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Return to Dashboard
                </button>
              </div>
            </div>
            <BulletinManagement />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

const StatCard = ({ title, value, icon, bgColor }) => (
  <div className={`${bgColor} rounded-lg shadow-sm p-4 flex items-center`}>
    <div className="mr-3 bg-white p-2 rounded-full shadow-sm">{icon}</div>
    <div>
      <p className="text-sm text-gray-600 mb-0.5">{title}</p>
      <p className="text-xl font-semibold text-gray-800">{value}</p>
    </div>
  </div>
);

const ActionButton = ({ title, icon, link, color }) => (
  <Link
    to={link}
    className={`${color} text-white py-2 px-3 rounded-md hover:opacity-90 transition-opacity flex items-center justify-center text-sm whitespace-nowrap`}
  >
    {icon}
    <span className="ml-2">{title}</span>
  </Link>
);

export default AdminDashboard;
